import {
  deleteAccessScope,
  updateAccessScopes,
  fetchCanViewManagementCost,
  fetchAccessScopesUsers,
  fetchFieldPermissions,
  fetchFeatures,
  fetchAccessGroups,
  updateAccessGroup,
  deleteAccessGroup,
  createAccessGroup,
  deleteAccessScopes,
  fetchAuditLogs,
  fetchAuditLogsCount
} from '@/services/accessScopes-api.service'
import * as Sentry from '@sentry/browser'
import { fetchAccessScopesMeta } from '@/services/accessScopes-api.service'
import { syncDayforceUsersAccessForBoard } from '@/services/import-api.service'
import { logger } from '@/utils/logger'

const buildUserLookup = (users = []) => {
  const lookup = {}

  users.forEach((user) => {
    const displayName = user?.name || user?.email || user?.uid
    if (user?.uid) {
      lookup[user.uid] = displayName
    }
    if (user?.email) {
      lookup[user.email] = displayName
    }
  })

  return lookup
}

const addDisplayNamesToAuditLogs = (auditLogs = [], userLookup = {}) => {
  return auditLogs.map((log) => ({
    ...log,
    performedByDisplay: userLookup[log.performedBy] || log.performedBy,
    targetUserDisplay: userLookup[log.targetUser] || log.targetUser
  }))
}

const getDefaultState = () => {
  return {
    departments: [],
    locations: [],
    managers: [],
    customFieldsTypes: [],
    customFieldsValues: [],
    sensitiveData: [],
    isFetched: false,
    canViewManagementCost: false,
    users: [],
    fieldPermissions: {},
    accessGroups: [],
    features: {},
    featuresFetched: false,
    featuresLoading: false, // Track loading state separately to avoid race conditions
    auditLogs: [],
    auditLogsCount: 0,
    auditLogsFetched: false,
    userLookup: {}
  }
}

const state = getDefaultState()

const mutations = {
  DELETE_ACCESS_SCOPE: (_state, { targetUid, scopeId }) => {
    const userIndex = _state.users.findIndex((u) => u.uid === targetUid)
    if (userIndex >= 0) {
      const user = _state.users[userIndex]
      const scopeInd = user.accessScopes?.findIndex((s) => s.scopeId === scopeId)
      if (scopeInd >= 0) {
        user.accessScopes.splice(scopeInd, 1)
      }
      _state.users[userIndex] = user
    }
  },
  DELETE_USER: (_state, uid) => {
    _state.users = _state.users.filter((u) => u.uid !== uid)
  },
  SET_DEPARTMENTS(_state, departments) {
    _state.departments = departments
  },
  SET_LOCATIONS(_state, locations) {
    _state.locations = locations
  },
  SET_MANAGERS(_state, managers) {
    _state.managers = managers
  },
  SET_CUSTOM_FIELDS_TYPES(_state, customFieldsTypes) {
    _state.customFieldsTypes = customFieldsTypes
  },
  SET_CUSTOM_FIELDS_VALUES(_state, customFieldsValues) {
    _state.customFieldsValues = customFieldsValues
  },
  SET_SENSITIVE_DATA(_state, sensitiveData) {
    _state.sensitiveData = sensitiveData
  },
  SET_IS_FETCHED(_state, isFetched) {
    _state.isFetched = isFetched
  },
  SET_CAN_VIEW_MANAGEMENT_COST(_state, canView) {
    _state.canViewManagementCost = canView
  },
  SET_USERS(_state, userInfo) {
    _state.users = userInfo
    _state.userLookup = buildUserLookup(userInfo)
    _state.auditLogs = addDisplayNamesToAuditLogs(_state.auditLogs, _state.userLookup)
  },
  SET_FIELD_PERMISSIONS(_state, fieldPermissions) {
    _state.fieldPermissions = fieldPermissions
  },
  SET_ACCESS_GROUPS(_state, accessGroups) {
    _state.accessGroups = accessGroups
  },
  SET_FEATURES(_state, features) {
    _state.features = features
  },
  SET_FEATURES_FETCHED(_state, featuresFetched) {
    _state.featuresFetched = featuresFetched
  },
  SET_FEATURES_LOADING(_state, loading) {
    _state.featuresLoading = loading
  },
  SET_AUDIT_LOGS(_state, auditLogs) {
    const lookup = _state.userLookup || buildUserLookup(_state.users)
    _state.auditLogs = addDisplayNamesToAuditLogs(auditLogs, lookup)
  },
  SET_AUDIT_LOGS_COUNT(_state, auditLogsCount) {
    _state.auditLogsCount = auditLogsCount
  },
  SET_AUDIT_LOGS_FETCHED(_state, auditLogsFetched) {
    _state.auditLogsFetched = auditLogsFetched
  },
  ADD_AUDIT_LOG(_state, auditEntry) {
    const lookup = _state.userLookup || buildUserLookup(_state.users)
    const [entryWithNames] = addDisplayNamesToAuditLogs([auditEntry], lookup)
    // Add new audit log entry to the beginning of the array (most recent first)
    _state.auditLogs.unshift(entryWithNames)
    _state.auditLogsCount += 1
  }
}

const getters = {
  departments: (_state) => _state.departments ?? [],
  locations: (_state) => _state.locations ?? [],
  managers: (_state) => _state.managers ?? [],
  customFieldsTypes: (_state) => _state.customFieldsTypes ?? [],
  customFieldsValues: (_state) => _state.customFieldsValues ?? [],
  sensitiveData: (_state) => _state.sensitiveData ?? [],
  isFetched: (_state) => _state.isFetched,
  canViewManagementCost: (_state) => _state.canViewManagementCost,
  users: (_state) => _state.users,
  fieldPermissions: (_state) => _state.fieldPermissions,
  accessGroups: (_state) => _state.accessGroups,
  features: (_state) => _state.features,
  featuresFetched: (_state) => _state.featuresFetched,
  featuresLoading: (_state) => _state.featuresLoading,
  auditLogs: (_state) => _state.auditLogs,
  auditLogsCount: (_state) => _state.auditLogsCount,
  auditLogsFetched: (_state) => _state.auditLogsFetched,
  userLookup: (_state) => _state.userLookup
}

const actions = {
  async deleteAccessScope(context, { boardId, scopeId, targetUid }) {
    try {
      await deleteAccessScope({ boardId, scopeId, targetUid })
      context.commit('DELETE_ACCESS_SCOPE', { targetUid, scopeId })
      // Refresh audit logs after successful change
      await context.dispatch('fetchAuditLogs', { boardId })
      return true
    } catch (error) {
      logger.error('Error in deleteAccessScope', error.message)
      Sentry?.captureException(error)
      return false
    }
  },

  async deleteUserAndScopes(context, { boardId, targetUid }) {
    try {
      await deleteAccessScopes({ boardId, targetUid })
      context.commit('DELETE_USER', targetUid)
      // Refresh audit logs after successful change
      await context.dispatch('fetchAuditLogs', { boardId })
      return true
    } catch (error) {
      logger.error('Error in deleteUserAndScopes', error.message)
      Sentry?.captureException(error)
      return false
    }
  },

  async updateAccessScopes({ commit, getters, dispatch }, { targetUid, accessScopes, boardId }) {
    try {
      const allUserScopes = await updateAccessScopes({
        targetUid,
        accessScopes,
        boardId
      })

      const users = getters.users
      const user = users.find((_user) => _user.uid === targetUid)
      if (user) {
        user.accessScopes = allUserScopes
        commit('SET_USERS', users)
      }

      // Refresh audit logs after successful change
      await dispatch('fetchAuditLogs', { boardId })
      return true
    } catch (error) {
      logger.error('Error in updateAccessScopes', error.message)
      Sentry?.captureException(error)
    }
  },

  async fetchAccessScopesMeta({ commit, getters }, { boardId, force = false }) {
    if (!force && getters.isFetched) return

    try {
      const result = await fetchAccessScopesMeta({ boardId })

      commit('SET_DEPARTMENTS', result.departments)
      commit('SET_LOCATIONS', result.locations)
      commit('SET_MANAGERS', result.managers)
      commit('SET_CUSTOM_FIELDS_TYPES', result.customFieldsTypes)
      commit('SET_CUSTOM_FIELDS_VALUES', result.customFieldsValues)
      commit('SET_SENSITIVE_DATA', result.sensitiveData)
      commit('SET_IS_FETCHED', true)
    } catch (e) {
      logger.error('fetchAccessScopesMeta err', e.message)
    }
  },

  async fetchCanViewManagementCost({ commit }, { boardId }) {
    try {
      const canView = await fetchCanViewManagementCost({ boardId })
      commit('SET_CAN_VIEW_MANAGEMENT_COST', canView)
    } catch (e) {
      logger.error('fetchCanViewManagementCost err', e.message)
    }
  },

  async fetchAccessScopesUsers({ commit }, { boardId }) {
    try {
      const result = await fetchAccessScopesUsers({ boardId })

      const hasDayforceAdminMismatch = result.some(
        (user) => user.isDayforceAdmin && user.accessLevel !== 'admin'
      )
      const hasDayforceMemberMismatch = result.some(
        (user) => user.isDayforceUser && !user.isDayforceAdmin && user.accessLevel === 'admin'
      )

      if (hasDayforceAdminMismatch || hasDayforceMemberMismatch) {
        await syncDayforceUsersAccessForBoard({ boardId }).catch((err) => {
          logger.warn('Auto-sync Dayforce admin access failed', err?.message)
        })
        const synced = await fetchAccessScopesUsers({ boardId })
        commit('SET_USERS', synced)
      } else {
        commit('SET_USERS', result)
      }
    } catch (e) {
      logger.error('fetchAccessScopesUsers err', e.message)
    }
  },

  async fetchFieldPermissions({ commit }, { boardId }) {
    try {
      const result = await fetchFieldPermissions({ boardId })
      commit('SET_FIELD_PERMISSIONS', result)
    } catch (e) {
      logger.error('fetchAccessScopesUsers err', e.message)
    }
  },

  async fetchFeatures({ commit, state }, { boardId }) {
    // Prevent duplicate fetches - if already loading for this board, skip
    if (state.featuresLoading) {
      return
    }

    try {
      // Set loading state but preserve the previous featuresFetched value
      // This prevents race conditions where checks happen during the loading window
      commit('SET_FEATURES_LOADING', true)

      const result = await fetchFeatures({ boardId })

      commit('SET_FEATURES', result)
      commit('SET_FEATURES_FETCHED', boardId)
    } catch (e) {
      logger.error('fetchFeatures err', e.message)
      // On error, only clear featuresFetched if it was for this board
      // This prevents clearing a successful fetch for a different board
      if (state.featuresFetched === boardId) {
        commit('SET_FEATURES_FETCHED', null)
      }
    } finally {
      commit('SET_FEATURES_LOADING', false)
    }
  },

  async fetchAccessGroups({ commit }, { boardId }) {
    try {
      const result = await fetchAccessGroups({ boardId })
      commit('SET_ACCESS_GROUPS', result)
    } catch (e) {
      logger.error('fetchAccessGroups err', e.message)
    }
  },

  async createAccessGroup({ commit, getters, dispatch }, { boardId, accessScope }) {
    try {
      const newGroup = await createAccessGroup({ boardId, accessScope })
      if (newGroup) {
        const groups = getters.accessGroups.concat(newGroup)
        commit('SET_ACCESS_GROUPS', groups)
        // Refresh audit logs after successful change
        await dispatch('fetchAuditLogs', { boardId })
        return true
      }
      return false
    } catch (e) {
      logger.error('createAccessGroup err', e.message)
      return false
    }
  },

  async updateAccessGroup({ commit, getters, dispatch }, { boardId, accessScope }) {
    try {
      const updatedGroup = await updateAccessGroup({ boardId, accessScope })
      if (updatedGroup) {
        const otherGroups = getters.accessGroups.filter((g) => g.scopeId !== updatedGroup.scopeId)
        commit('SET_ACCESS_GROUPS', otherGroups.concat(updatedGroup))
        // Refresh audit logs after successful change
        await dispatch('fetchAuditLogs', { boardId })
        return true
      }
      return false
    } catch (e) {
      logger.error('updateAccessGroup err', e.message)
      return false
    }
  },

  async deleteAccessGroup({ commit, getters, dispatch }, { boardId, scopeId }) {
    try {
      const deleted = await deleteAccessGroup({ boardId, scopeId })
      if (deleted) {
        const otherGroups = getters.accessGroups.filter((g) => g.scopeId !== scopeId)
        commit('SET_ACCESS_GROUPS', otherGroups)
        // Refresh audit logs after successful change
        await dispatch('fetchAuditLogs', { boardId })
        return true
      }
      return false
    } catch (e) {
      logger.error('deleteAccessGroup err', e.message)
      return false
    }
  },
  // Audit Log Actions
  async fetchAuditLogs(
    { commit, state },
    { boardId, limit = 100, offset = 0, sortBy = 'timestamp', sortOrder = 'DESC' }
  ) {
    try {
      const auditLogs = await fetchAuditLogs({ boardId, limit, offset, sortBy, sortOrder })
      const count = await fetchAuditLogsCount({ boardId })
      const lookup = state.userLookup || buildUserLookup(state.users)
      const auditLogsWithNames = addDisplayNamesToAuditLogs(auditLogs, lookup)
      commit('SET_AUDIT_LOGS', auditLogsWithNames)
      commit('SET_AUDIT_LOGS_COUNT', count)
      commit('SET_AUDIT_LOGS_FETCHED', true)
      return true
    } catch (e) {
      logger.error('fetchAuditLogs err', e.message)
      commit('SET_AUDIT_LOGS_FETCHED', false)
      return false
    }
  },

  async fetchAuditLogsCount({ commit }, { boardId }) {
    try {
      const count = await fetchAuditLogsCount({ boardId })
      commit('SET_AUDIT_LOGS_COUNT', count)
      return count
    } catch (e) {
      logger.error('fetchAuditLogsCount err', e.message)
      return 0
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
  modules: {}
}
